import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Download } from "lucide-react";
import { getVzDealerReport, getVzAdminReport } from "@/lib/verizonSalesApi";

const fmt = (col, v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    if (/%|mix|rewards$/i.test(col) && Math.abs(v) <= 5) return `${(v * 100).toFixed(1)}%`;
    if (/tsp|zip|phone|\bid\b/i.test(col)) return String(Math.round(v));
    return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
};

export const isAvgCol = (c) => /%|mix|rewards$|pct|rate/i.test(c);
export const isIdCol = (c) => /tsp|zip|phone|\bid\b|date|year/i.test(c);

// Sales Dealer Report grid (every DLAR column) with a sticky totals row
export const DealerReportTable = ({ columns, rows }) => (
    <div className="overflow-auto max-h-[520px] border-t border-slate-200" data-testid="sales-dealer-report-table-wrap">
      <table className="text-xs border-collapse" data-testid="sales-dealer-report-table">
        <thead className="sticky top-0 z-20">
          <tr>
            {columns.map((c, i) => (
              <th key={c} className={`bg-navy text-white font-semibold px-2.5 py-2 text-left whitespace-nowrap border-r border-white/10 ${i === 0 ? "sticky left-0 z-30" : ""}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="odd:bg-white even:bg-slate-50 hover:bg-teal/5" data-testid={`sales-dealer-report-row-${ri}`}>
              {r.map((v, i) => (
                <td key={i} className={`px-2.5 py-1.5 whitespace-nowrap border-r border-b border-slate-100 ${i === 0 ? "sticky left-0 bg-inherit font-semibold text-navy z-10" : ""} ${typeof v === "number" ? "text-right tabular-nums" : ""}`}>
                  {fmt(columns[i], v)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot className="sticky bottom-0 z-20">
          <tr className="bg-slate-100 font-semibold text-navy" data-testid="sales-dealer-report-totals">
            {columns.map((c, i) => {
              if (i === 0) return <td key={c} className="px-2.5 py-1.5 sticky left-0 bg-slate-100 z-30 whitespace-nowrap">Total ({rows.length})</td>;
              const vals = rows.map((r) => r[i]).filter((v) => typeof v === "number");
              if (!vals.length || isIdCol(c)) return <td key={c} />;
              const avg = isAvgCol(c);
              const t = vals.reduce((a, b) => a + b, 0) / (avg ? vals.length : 1);
              return <td key={c} className="px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap border-r border-slate-200">{fmt(c, Math.round(t * 1000) / 1000)}</td>;
            })}
          </tr>
        </tfoot>
      </table>
    </div>
);

const toCsv = (cols, rows) => [cols, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");

export { fmt as fmtDlar };

export const SalesDealerReport = ({ admin = false, month, monthText }) => {
  const [data, setData] = useState(null);
  const [dealer, setDealer] = useState("");
  const [dealers, setDealers] = useState([]);
  useEffect(() => {
    (admin ? getVzAdminReport(dealer) : getVzDealerReport(month))
      .then((d) => { setData(d); if (d.dealers) setDealers(d.dealers); })
      .catch(() => setData({ columns: [], rows: [] }));
  }, [admin, dealer, month]);
  if (!data) return null;

  const download = () => {
    const blob = new Blob([toCsv(data.columns, data.rows.map((r) => r.map((v, i) => fmt(data.columns[i], v))))], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `sales-dealer-report-${admin ? "admin-" : ""}${data.updated_through || "latest"}.csv`; a.click();
  };

  return (
    <Card className="border-slate-200" data-testid="sales-dealer-report">
      <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-teal" /> Sales Dealer Report</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">{monthText ? `Showing ${monthText} · ` : ""}{data.rows.length} stores · {data.columns.length} columns · data through {data.updated_through || "—"}</p>
        </div>
        <div className="flex items-center gap-2">
        {admin && (
          <select value={dealer} onChange={(e) => setDealer(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm max-w-[240px]" data-testid="sales-dealer-report-dealer-filter">
            <option value="">All dealers</option>
            {dealers.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.stores})</option>)}
            <option value="unassigned">Unassigned stores</option>
          </select>
        )}
        <Button size="sm" variant="outline" onClick={download} disabled={!data.rows.length} data-testid="sales-dealer-report-download"><Download className="w-4 h-4 mr-1" /> Download CSV</Button>
        </div>
      </CardHeader>
      <CardContent className="px-0">
        {data.columns.length === 0 ? <p className="text-sm text-slate-500 px-6 py-6">The report will appear after the next Verizon sync.</p> : (
          <DealerReportTable columns={data.columns} rows={data.rows} />
        )}
      </CardContent>
    </Card>
  );
};
